import { CancelToken } from 'axios';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getInvitationList, invitationListActions, selectInvitationList } from '../../store/entities/invitationList';
import { selectIdentity } from '../../store/entities/identity';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { Grid } from '../common/grid/Grid';
import { SectionHeader } from '../common/SectionHeader';
import { CellDate } from '../common/grid/CellDate';
import { InvitationFilter } from './InvitationFilter';
import { hasRole } from '../../utils/roles';
import { getLangNameObj } from '../../utils/format';
import { FaIcon } from '../common/FaIcon';
import { faPersonWalking, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@mui/material';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { InvitationType } from '@api/models/enums/InvitationType';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { UserRole } from '@api/models/enums/UserRole';

export const Invitation: FC = () => {
  const { t, i18n } = useTranslation();
  const identity = useAppSelector(selectIdentity);
  const invitationList = useAppSelector(selectInvitationList);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const businessSettings = useBusinessSettings();

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getInvitationList(cancelToken));
  };

  const handleRenderData = (item: InvitationDto) => {
    return (
      <>
        <QcsTableCell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(item.type === InvitationType.OneTime || item.type === InvitationType.OneTimeEntry) && (
              <Tooltip title={t('invitation.visitInvitation')}>
                <span>
                  <FaIcon icon={faPersonWalking} style={{ marginRight: '0.5rem' }} />
                </span>
              </Tooltip>
            )}
            {item.entryRequestRequired && businessSettings.allowEntryRequests && (
              <FaIcon icon={faSignInAlt} color={'error'} style={{ marginRight: '0.5rem' }} />
            )}
            {getLangNameObj(i18n, item)}
          </div>
        </QcsTableCell>
        <QcsTableCell>{getLangNameObj(i18n, item.supplier)}</QcsTableCell>
        <CellDate value={item.validFrom} hideOnMobile={true} />
        <CellDate value={item.validTo} hideOnMobile={true} />
        <QcsTableCell hideOnMobile={true}>{t('invitation.workerStatus.' + item.workerStatus)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{t('invitation.states.' + item.state)}</QcsTableCell>
      </>
    );
  };

  const handleRowClick = (item: InvitationDto) => {
    navigate(
      `/invitation/${item.id}/${
        item.type === InvitationType.OneTime || item.type === InvitationType.OneTimeEntry || hasRole(identity.roles, [UserRole.ExternalWorker])
          ? 'person'
          : 'detail'
      }`,
    );
  };

  const handleAddClick = () => {
    navigate('/invitation/new');
  };

  return (
    <>
      <SectionHeader
        title="invitation.title"
        addText="invitation.add"
        handleAddClick={hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]) ? handleAddClick : undefined}
      />
      <Grid<InvitationDto>
        headers={[
          { captionStr: 'invitation.name', orderName: 'name' },
          { captionStr: 'invitation.supplier' },
          {
            captionStr: 'invitation.validFrom',
            orderName: 'validFrom',
            hideOnMobile: true,
          },
          {
            captionStr: 'invitation.validTo',
            orderName: 'validTo',
            hideOnMobile: true,
          },
          {
            captionStr: 'invitation.invitationWorkerState',
            orderName: 'workerStatus',
            hideOnMobile: true,
          },
          {
            captionStr: 'invitation.state',
            orderName: 'state',
            hideOnMobile: true,
          },
        ]}
        data={invitationList}
        gridActions={invitationListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
        search={true}
        filter={<InvitationFilter query={invitationList.query} />}
      />
    </>
  );
};
